import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { SPEED_OPTIONS } from '@/constants/speed-options';

import { VideoPlayerHandlers } from './useVideoPlayerHandlers';

// stepShuttleRate walks this ascending; do not reorder.
const FORWARD_RATES = SPEED_OPTIONS.map(option => option.value).sort((a, b) => a - b);
const SHUTTLE_RATES = [...[...FORWARD_RATES].reverse().map(rate => -rate), ...FORWARD_RATES];
const REVERSE_TICK_MS = 50;
const PROGRESS_BAR_SEEK_SECONDS = 5;
const PROGRESS_BAR_SELECTOR = '[data-video-progress-bar]';
const FORM_ELEMENT_TAGS = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'];

function stepShuttleRate(current: number, direction: 1 | -1): number {
  const next =
    direction === 1
      ? SHUTTLE_RATES.find(rate => rate > current)
      : [...SHUTTLE_RATES].reverse().find(rate => rate < current);

  return next ?? current;
}

const isFormElement = (element: HTMLElement): boolean =>
  FORM_ELEMENT_TAGS.includes(element.tagName) || element.isContentEditable;

export function useVideoKeyboardHandlers(
  handlers: VideoPlayerHandlers,
  state: {
    played: number;
    duration: number;
    currentVolume: number;
    isPlaying: boolean;
    currentRate: number;
  },
  containerRef: RefObject<HTMLDivElement>
): { stopRewind: () => void; reverseRate: number | null } {
  const [isRewinding, setIsRewinding] = useState(false);
  const [isForwarding, setIsForwarding] = useState(false);
  const [shuttleRate, setShuttleRate] = useState<number | null>(null);
  const rewindIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const forwardIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reverseIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stateRef = useRef(state);
  const handlersRef = useRef(handlers);
  const shuttleRateRef = useRef<number>(state.currentRate);
  const seekTargetRef = useRef<number>(state.played);

  const reverseRate = shuttleRate !== null && shuttleRate < 0 ? shuttleRate : null;

  handlersRef.current = handlers;

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    seekTargetRef.current = state.played;
  }, [state.played]);

  const stepSeekTarget = useCallback((deltaSeconds: number): number | null => {
    const { duration } = stateRef.current;

    if (!duration) {
      return null;
    }

    const currentSeconds = seekTargetRef.current * duration;
    // Rounded to the millisecond: without it repeated steps land just under the whole
    // second and the time read-out floors to the previous one.
    const nextSeconds = Math.min(
      duration,
      Math.max(0, Math.round((currentSeconds + deltaSeconds) * 1000) / 1000)
    );
    const nextPlayed = nextSeconds / duration;

    seekTargetRef.current = nextPlayed;

    return nextPlayed;
  }, []);

  useEffect(() => {
    if (shuttleRate === null) {
      shuttleRateRef.current = state.currentRate;
    }
  }, [state.currentRate, shuttleRate]);

  // Must stay in an effect. Called inline from the key handler, a burst crossing a
  // positive rung fires a play/pause pair that React collapses into no prop change,
  // leaving the element playing while the timeline walks backwards.
  useEffect(() => {
    if (shuttleRate === null || shuttleRate < 0) {
      return;
    }

    handlersRef.current.handlePlaybackRateChange(shuttleRate);
    handlersRef.current.handlePlay();
  }, [shuttleRate]);

  useEffect(() => {
    if (isRewinding) {
      if (stateRef.current.isPlaying) {
        handlers.handlePause();
      }

      const rate = stateRef.current.currentRate;
      const stepSize = (2 * rate) / stateRef.current.duration;

      rewindIntervalRef.current = setInterval(() => {
        const currentPlayed = stateRef.current.played;
        const newPlayed = Math.max(0, currentPlayed - stepSize);

        if (newPlayed > 0) {
          handlers.handleSeek(newPlayed);
        } else {
          handlers.handleSeek(0);
          setIsRewinding(false);
        }
      }, 50);
    } else {
      if (rewindIntervalRef.current) {
        clearInterval(rewindIntervalRef.current);
        rewindIntervalRef.current = null;
      }
    }

    return () => {
      if (rewindIntervalRef.current) {
        clearInterval(rewindIntervalRef.current);
      }
    };
  }, [isRewinding, handlers]);

  useEffect(() => {
    if (isForwarding) {
      if (stateRef.current.isPlaying) {
        handlers.handlePause();
      }

      const rate = stateRef.current.currentRate;
      const stepSize = (2 * rate) / stateRef.current.duration;

      forwardIntervalRef.current = setInterval(() => {
        const currentPlayed = stateRef.current.played;
        const newPlayed = Math.min(1, currentPlayed + stepSize);

        if (newPlayed < 1) {
          handlers.handleSeek(newPlayed);
        } else {
          handlers.handleSeek(1);
          setIsForwarding(false);
        }
      }, 50);
    } else {
      if (forwardIntervalRef.current) {
        clearInterval(forwardIntervalRef.current);
        forwardIntervalRef.current = null;
      }
    }

    return () => {
      if (forwardIntervalRef.current) {
        clearInterval(forwardIntervalRef.current);
      }
    };
  }, [isForwarding, handlers]);

  useEffect(() => {
    if (reverseRate === null) {
      return;
    }

    // Unconditional: a burst can cross a positive rung, whose handlePlay would leave the
    // element running forward underneath the backwards seeking.
    handlersRef.current.handlePause();

    reverseIntervalRef.current = setInterval(() => {
      const newPlayed = stepSeekTarget(-Math.abs(reverseRate) * (REVERSE_TICK_MS / 1000));

      if (newPlayed === null) {
        return;
      }

      handlersRef.current.handleSeek(newPlayed);

      if (newPlayed === 0) {
        setShuttleRate(null);
      }
    }, REVERSE_TICK_MS);

    return () => {
      if (reverseIntervalRef.current) {
        clearInterval(reverseIntervalRef.current);
        reverseIntervalRef.current = null;
      }
    };
  }, [reverseRate, stepSeekTarget]);

  const stopRewind = useCallback(() => {
    setIsRewinding(false);
    setIsForwarding(false);
    setShuttleRate(null);
    // Do not reset shuttleRateRef here: handlePlay routes back in with stateRef still
    // holding the previous rate, which would pin the ladder and block reverse.
  }, []);

  const applyShuttleRate = useCallback((rate: number) => {
    shuttleRateRef.current = rate;

    if (rate < 0) {
      setIsRewinding(false);
      setIsForwarding(false);
    }

    setShuttleRate(rate);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      event.stopPropagation();

      const target = event.target as HTMLElement;
      const isInputFocused =
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;

      const container = containerRef.current;
      const isInsidePlayer = Boolean(container && target && container.contains(target));
      const isProgressBarFocused = isInsidePlayer && target.matches(PROGRESS_BAR_SELECTOR);
      const isVideoFocused = isInsidePlayer && !isFormElement(target);
      const isHorizontalArrow = event.key === 'ArrowRight' || event.key === 'ArrowLeft';
      const hasOnlyShift = event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey;
      const hasNoModifier = !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey;

      if (isHorizontalArrow && hasOnlyShift && isProgressBarFocused) {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextPlayed = stepSeekTarget(direction * PROGRESS_BAR_SEEK_SECONDS);

        if (nextPlayed !== null) {
          handlers.handleSeek(nextPlayed);
        }
        return;
      }

      if (isHorizontalArrow && hasNoModifier && isVideoFocused) {
        event.preventDefault();
        applyShuttleRate(
          stepShuttleRate(shuttleRateRef.current, event.key === 'ArrowRight' ? 1 : -1)
        );
        return;
      }

      if (event.ctrlKey && event.key === 'ArrowUp') {
        event.preventDefault();
        if (isRewinding) {
          setIsRewinding(false);
        }
        if (isForwarding) {
          setIsForwarding(false);
        }
        handlers.handlePlay();
      }
      if (event.ctrlKey && event.key === 'ArrowDown') {
        event.preventDefault();
        // Reverse leaves the element paused already, so handlePause alone is a no-op.
        setShuttleRate(null);
        handlers.handlePause();
      }
      if (event.ctrlKey && event.key === 'ArrowRight') {
        event.preventDefault();
        setShuttleRate(null);
        setIsForwarding(prev => !prev);
        if (isRewinding) {
          setIsRewinding(false);
        }
      }
      if (event.ctrlKey && event.key === 'ArrowLeft') {
        event.preventDefault();
        setShuttleRate(null);
        setIsRewinding(prev => !prev);
        if (isForwarding) {
          setIsForwarding(false);
        }
      }
      if (event.key === ',' && !isInputFocused) {
        event.preventDefault();
        handlers.handleSeek(state.played - 1 / (state.duration * 30));
      }
      if (event.key === '.' && !isInputFocused) {
        event.preventDefault();
        handlers.handleSeek(state.played + 1 / (state.duration * 30));
      }
      if (event.ctrlKey && event.key === 'F10') {
        event.preventDefault();
        handlers.handleFullscreen();
      }
      if (event.key.toLowerCase() === 'home') {
        event.preventDefault();
        handlers.handleSeek(0);
      }
      if (event.key.toLowerCase() === 'end') {
        event.preventDefault();
        handlers.handleSeek(1);
      }
      if (event.ctrlKey && event.shiftKey && event.key === '+') {
        event.preventDefault();
        if (state.currentVolume > 0.9 && state.currentVolume < 1) {
          handlers.handleVolumeChange(1);
        }
        if (state.currentVolume <= 0.9) {
          handlers.handleVolumeChange(state.currentVolume + 0.1);
        }
      }
      if (event.ctrlKey && event.shiftKey && event.key === '-') {
        event.preventDefault();
        if (state.currentVolume > 0 && state.currentVolume < 0.1) {
          handlers.handleVolumeChange(0);
        }
        if (state.currentVolume >= 0.1) {
          handlers.handleVolumeChange(state.currentVolume - 0.1);
        }
      }
    },
    [
      handlers.handlePlay,
      handlers.handlePause,
      handlers.handleSeek,
      handlers.handleFullscreen,
      handlers.handleVolumeChange,
      handlers.handlePlaybackRateChange,
      state.played,
      state.duration,
      state.currentVolume,
      isRewinding,
      isForwarding,
      setIsRewinding,
      setIsForwarding,
      containerRef,
      applyShuttleRate,
      stepSeekTarget,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { stopRewind, reverseRate };
}
